import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, Loading, StatusBadge, inr, timeAgo, initials } from "@/components/common";
import { ROLE_LABELS } from "@/lib/nav";
import MyCampaigns from "@/components/MyCampaigns";
import CallsReport from "@/components/CallsReport";
import LeaveFollowups from "@/components/LeaveFollowups";
import SourceStats from "@/components/SourceStats";
import {
  Users, PhoneCall, PhoneMissed, CheckCircle2, CalendarClock, AlertTriangle,
  GraduationCap, FileText, Award, Wallet, Flame, MessageSquare, TrendingUp, ShieldAlert,
} from "lucide-react";

function Kpi({ icon: Icon, label, value, tone = "blue", onClick, testid }) {
  const tones = {
    blue: "text-[#312E81] bg-blue-50", orange: "text-[#4F46E5] bg-orange-50",
    green: "text-emerald-600 bg-emerald-50", rose: "text-rose-600 bg-rose-50",
    violet: "text-violet-600 bg-violet-50", amber: "text-amber-600 bg-amber-50",
  };
  return (
    <button onClick={onClick} data-testid={testid}
      className="text-left bg-white border border-slate-200 rounded-xl p-4 shadow-sm hover:shadow-md hover:border-[#4F46E5]/40 transition-all">
      <div className={`w-9 h-9 rounded-lg flex items-center justify-center mb-3 ${tones[tone]}`}><Icon className="w-[18px] h-[18px]" /></div>
      <div className="font-heading text-2xl font-extrabold text-[#312E81] tracking-tight">{value}</div>
      <div className="text-xs text-slate-500 mt-0.5">{label}</div>
    </button>
  );
}

export default function Dashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [kpis, setKpis] = useState(null);
  const [funnel, setFunnel] = useState([]);
  const [board, setBoard] = useState([]);
  const [tops, setTops] = useState(null);
  const [acts, setActs] = useState([]);
  const [leakage, setLeakage] = useState(null);
  const [cc, setCc] = useState(null);
  const [todayFu, setTodayFu] = useState([]);

  const isManager = ["super_admin", "admin", "admission_manager", "management"].includes(user.role);
  const hideRevenue = ["counselor", "telecaller"].includes(user.role);

  useEffect(() => {
    api.get("/dashboard/kpis").then(({ data }) => setKpis(data));
    api.get("/dashboard/funnel").then(({ data }) => setFunnel(data));
    if (isManager) api.get("/dashboard/leaderboard").then(({ data }) => setBoard(data)).catch(() => {});
    else api.get("/dashboard/top-performers").then(({ data }) => setTops(data)).catch(() => {});
    api.get("/activities?limit=12").then(({ data }) => setActs(data));
    api.get("/followups?bucket=due_today").then(({ data }) => setTodayFu(data)).catch(() => {});
    api.get("/dashboard/command-center").then(({ data }) => setCc(data)).catch(() => {});
    if (isManager) api.get("/dashboard/leakage").then(({ data }) => setLeakage(data)).catch(() => {});
  }, []);

  if (!kpis) return <Loading label="Loading command center" />;

  const maxFunnel = Math.max(...funnel.map((f) => f.count), 1);

  return (
    <div>
      <PageHeader title={`Good to see you, ${user.name.split(" ")[0]}`} subtitle={`${ROLE_LABELS[user.role]} · Command Center · Today`} testid="dashboard-header" />

      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-4 xl:grid-cols-6 gap-3 sm:gap-4">
        <Kpi icon={Users} label="New Leads Today" value={kpis.new_leads} tone="blue" onClick={() => navigate("/leads")} testid="kpi-new-leads" />
        <Kpi icon={Flame} label="Hot Leads" value={kpis.hot_leads} tone="rose" onClick={() => navigate("/leads?category=Hot")} testid="kpi-hot" />
        <Kpi icon={PhoneCall} label="Calls Today" value={kpis.calls_today} tone="orange" onClick={() => navigate("/calls")} testid="kpi-calls" />
        <Kpi icon={CheckCircle2} label="Connected" value={kpis.connected} tone="green" />
        <Kpi icon={CalendarClock} label="Follow-ups Due" value={kpis.followups_due} tone="amber" onClick={() => navigate("/followups")} testid="kpi-fu-due" />
        <Kpi icon={AlertTriangle} label="Overdue" value={kpis.followups_overdue} tone="rose" onClick={() => navigate("/followups")} testid="kpi-fu-overdue" />
        <Kpi icon={GraduationCap} label="Counselling" value={kpis.counselling} tone="violet" onClick={() => navigate("/counselling")} />
        <Kpi icon={FileText} label="Applications" value={kpis.applications} tone="orange" onClick={() => navigate("/applications")} />
        <Kpi icon={Award} label="Admissions" value={kpis.admissions} tone="green" onClick={() => navigate("/admissions")} testid="kpi-admissions" />
        <Kpi icon={MessageSquare} label="WhatsApp Today" value={kpis.whatsapp} tone="green" />
        {!hideRevenue && <Kpi icon={Wallet} label="Revenue Recorded" value={inr(kpis.revenue)} tone="blue" testid="kpi-revenue" />}
        <Kpi icon={PhoneMissed} label="Pending Payments" value={inr(kpis.pending_payments)} tone="rose" testid="kpi-pending-payments" />
      </div>

      <SourceStats />
      <CallsReport />
      <MyCampaigns />
      {["team_lead", "admission_manager"].includes(user.role) && <LeaveFollowups />}

      <div className="grid lg:grid-cols-3 gap-4 sm:gap-6 mt-6">
        {/* Today's Follow-ups (TeleCRM-style focus block) */}
        <div className="lg:col-span-1 bg-white border border-slate-200 rounded-xl p-5 shadow-sm order-first lg:order-none">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-heading font-bold text-[#312E81]">Today's Follow-ups</h3>
            <button onClick={() => navigate("/followups")} className="text-xs font-semibold text-[#4F46E5] hover:underline" data-testid="see-all-fu">See all</button>
          </div>
          <div className="space-y-2 max-h-[320px] overflow-y-auto">
            {todayFu.length === 0 && <div className="text-sm text-slate-400 py-6 text-center">All caught up 🎉</div>}
            {todayFu.slice(0, 8).map((fu) => (
              <button key={fu.id} onClick={() => fu.student_id && navigate(`/students/${fu.student_id}`)}
                data-testid="today-fu-item"
                className="w-full text-left flex items-center gap-3 p-2.5 rounded-lg border border-slate-100 hover:border-[#4F46E5]/40 hover:bg-orange-50/40 transition-all">
                <div className="w-8 h-8 rounded-lg bg-amber-50 text-amber-600 flex items-center justify-center shrink-0"><CalendarClock className="w-4 h-4" /></div>
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-semibold text-slate-800 truncate">{fu.type}</div>
                  <div className="text-[11px] text-slate-400 truncate">{fu.notes || fu.owner_name}</div>
                </div>
                <StatusBadge value={fu.type} />
              </button>
            ))}
          </div>
        </div>

        {/* Funnel */}
        <div className="lg:col-span-2 bg-white border border-slate-200 rounded-xl p-5 shadow-sm">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-heading font-bold text-[#312E81]">Admission Funnel</h3>
            <TrendingUp className="w-4 h-4 text-[#4F46E5]" />
          </div>
          <div className="space-y-2.5">
            {funnel.map((f) => (
              <button key={f.stage} onClick={() => navigate(`/leads?stage=${encodeURIComponent(f.stage)}`)}
                data-testid={`funnel-${f.stage.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                className="w-full flex items-center gap-3 group">
                <div className="w-36 text-xs font-medium text-slate-600 text-right shrink-0">{f.stage}</div>
                <div className="flex-1 h-7 bg-slate-100 rounded-md overflow-hidden relative">
                  <div className="h-full rounded-md bg-gradient-to-r from-[#312E81] to-[#6366F1] group-hover:from-[#4F46E5] group-hover:to-[#818CF8] transition-all flex items-center justify-end pr-2"
                    style={{ width: `${Math.max((f.count / maxFunnel) * 100, 6)}%` }}>
                    <span className="text-[11px] font-bold text-white">{f.count}</span>
                  </div>
                </div>
              </button>
            ))}
          </div>
        </div>

        {/* Activity feed */}
        <div className="lg:col-span-3 bg-white border border-slate-200 rounded-xl p-5 shadow-sm">
          <h3 className="font-heading font-bold text-[#312E81] mb-4">Live Activity</h3>
          <div className="space-y-3 max-h-[360px] overflow-y-auto">
            {acts.map((a) => (
              <div key={a.id} className="flex gap-3">
                <div className="w-7 h-7 rounded-lg bg-[#312E81] text-white flex items-center justify-center text-[10px] font-bold shrink-0">{initials(a.actor_name)}</div>
                <div className="min-w-0">
                  <p className="text-xs text-slate-700"><span className="font-semibold">{a.actor_name}</span> {a.action}{a.student_name ? <> · <span className="font-semibold text-[#4F46E5]" data-testid="activity-candidate-name">{a.student_name}</span></> : ""}</p>
                  <p className="text-[11px] text-slate-400">{timeAgo(a.created_at)}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Leakage (managers) */}
      {isManager && leakage && (
        <div className="mt-6 bg-white border border-slate-200 rounded-xl p-5 shadow-sm">
          <div className="flex items-center gap-2 mb-4">
            <ShieldAlert className="w-5 h-5 text-rose-600" />
            <h3 className="font-heading font-bold text-[#312E81]">Leakage Report — needs attention</h3>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
            {[["Unassigned", leakage.unassigned], ["No First Call", leakage.no_first_call], ["No Follow-up", leakage.no_followup],
              ["Overdue Follow-ups", leakage.overdue_followups], ["Apps Stuck", leakage.applications_stuck], ["Payments Pending", leakage.payments_pending]].map(([l, v]) => (
              <div key={l} className="rounded-lg border border-rose-100 bg-rose-50/50 p-3">
                <div className="font-heading text-xl font-extrabold text-rose-600">{v}</div>
                <div className="text-[11px] text-slate-500">{l}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Leaderboard */}
      {!isManager && <TopPerformers tops={tops} />}
      {isManager && <div className="mt-6 bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden">
        <div className="p-5 flex items-center justify-between">
          <h3 className="font-heading font-bold text-[#312E81]">Counselor Leaderboard</h3>
          {cc && <div className="text-xs text-slate-500">Target <span className="font-bold text-[#312E81]">{cc.achieved}/{cc.target}</span> admissions</div>}
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-slate-50 border-y border-slate-200 text-[11px] uppercase tracking-wider text-slate-500">
                {["#", "Counselor", "Assigned", "Calls", "Connected", "Follow-ups", "Overdue", "Apps", "Admissions", "Conv %"].map((h) => (
                  <th key={h} className="text-left font-semibold px-4 py-2.5 whitespace-nowrap">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {board.map((r, i) => (
                <tr key={r.id} className="hover:bg-orange-50/30">
                  <td className="px-4 py-2.5">{i === 0 ? "🏆" : i + 1}</td>
                  <td className="px-4 py-2.5">
                    <div className="flex items-center gap-2">
                      <div className="w-7 h-7 rounded-lg bg-[#312E81] text-white flex items-center justify-center text-[10px] font-bold">{initials(r.name)}</div>
                      <div><div className="font-semibold text-slate-800">{r.name}</div><div className="text-[11px] text-slate-400">{r.team}</div></div>
                    </div>
                  </td>
                  <td className="px-4 py-2.5">{r.assigned}</td>
                  <td className="px-4 py-2.5">{r.calls}</td>
                  <td className="px-4 py-2.5 text-emerald-600 font-semibold">{r.connected}</td>
                  <td className="px-4 py-2.5">{r.followups_completed}</td>
                  <td className="px-4 py-2.5 text-rose-600 font-semibold">{r.overdue}</td>
                  <td className="px-4 py-2.5">{r.applications}</td>
                  <td className="px-4 py-2.5 font-bold text-[#4F46E5]">{r.admissions}</td>
                  <td className="px-4 py-2.5">{r.conversion}%</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>}
    </div>
  );
}

function TopList({ title, rows, testid }) {
  const medal = ["bg-amber-400", "bg-slate-400", "bg-orange-600"];
  return (
    <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-5" data-testid={testid}>
      <h3 className="font-heading font-bold text-[#312E81] mb-3">{title}</h3>
      <div className="space-y-2">
        {(rows || []).map((r, i) => (
          <div key={i} className={`flex items-center gap-3 p-2.5 rounded-lg ${r.is_me ? "bg-indigo-50 border border-indigo-200" : "bg-slate-50"}`} data-testid={`${testid}-row-${i}`}>
            <span className={`w-7 h-7 rounded-full ${medal[i]} text-white text-xs font-bold flex items-center justify-center`}>{i + 1}</span>
            <div className="w-8 h-8 rounded-lg bg-[#312E81] text-white flex items-center justify-center text-[10px] font-bold">{initials(r.name)}</div>
            <div className="flex-1 font-semibold text-slate-800">{r.name}{r.is_me && <span className="ml-2 text-[10px] text-[#4F46E5]">You</span>}</div>
            <div className="text-right"><div className="font-heading font-extrabold text-xl text-[#4F46E5]">{r.admissions}</div><div className="text-[10px] text-slate-400">admissions</div></div>
          </div>
        ))}
        {rows && !rows.length && <div className="text-sm text-slate-400">No admissions yet</div>}
      </div>
    </div>
  );
}

function TopPerformers({ tops }) {
  return (
    <div className="mt-6 grid md:grid-cols-2 gap-4" data-testid="top-performers">
      <TopList title="Top 3 Performers · This Month" rows={tops?.month} testid="top-month" />
      <TopList title="Top 3 Performers · Overall" rows={tops?.overall} testid="top-overall" />
    </div>
  );
}
